import { z } from 'zod';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {return value;}
  if (typeof value === 'string' && value.trim() !== '' && /^-?\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return NaN;
};

export const MAX_SLIDER_LABELS = 10;

const labelOption = z.object({ value: z.string(), label: z.string() });

export const getSliderSchema = () =>
  z
    .object({
      message: z.string().min(1, 'Message is required'),
      description: z.string().optional(),
      minLabel: z.string().optional(),
      maxLabel: z.string().optional(),
      questionId: z.string().max(255, 'Question ID max length exceeded (255)').optional(),
      min: z.union([z.number(), z.string()]).optional(),
      max: z.union([z.number(), z.string()]).optional(),
      increment: z.union([z.number(), z.string()]).optional(),
      response: z.array(labelOption).optional(),
      required: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      const labels = Array.isArray(data.response) ? data.response : [];
      const isLabeled = labels.length > 0;

      // ----- Labeled mode: validate the options, ignore min/max/increment ----
      if (isLabeled) {
        if (labels.length < 2) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['response'], message: 'Add at least 2 labels' });
        }
        if (labels.length > MAX_SLIDER_LABELS) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['response'], message: 'No more than 10 labels' });
        }
        const hasEmpty = labels.some((o) => !o.label || o.label.trim() === '');
        if (hasEmpty) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['response'], message: 'Labels cannot be empty' });
        }
        const hasSpecial = labels.some((o) => o.label?.startsWith('<>') || o.label?.startsWith('<none>'));
        if (hasSpecial) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['response'], message: 'Special options are not allowed on a slider' });
        }
        const seen = new Set<string>();
        const hasDuplicate = labels.some((o) => {
          const key = (o.label ?? '').trim();
          if (key === '') {return false;} // empty handled by the check above
          if (seen.has(key)) {return true;}
          seen.add(key);
          return false;
        });
        if (hasDuplicate) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['response'], message: 'Labels must be unique' });
        }
        return;
      }

      // ----- Numeric mode: existing rules ------------------------------------
      const min = toNumber(data.min);
      const max = toNumber(data.max);
      const increment = toNumber(data.increment);

      if (Number.isNaN(min)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['min'], message: 'Min value is required' });
      }
      if (Number.isNaN(max)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max'], message: 'Max value is required' });
      }
      if (Number.isNaN(increment) || increment <= 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['increment'], message: 'Increment is required' });
      }

      if (Number.isNaN(min) || Number.isNaN(max)) {return;}

      if (min >= max) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['min'], message: 'Min value must be less than Max value' });
        return;
      }

      const range = max - min;
      if (range > 100) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max'], message: 'The range between Min and Max value cannot exceed 100' });
      }
      if (!Number.isNaN(increment) && increment > 0) {
        if (increment >= range) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['increment'], message: 'Increment must be less than the range between Min and Max value' });
        } else if (range % increment !== 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['increment'], message: 'Increment must divide evenly into the range' });
        }
      }
    });
